// Base dungeon templates. ADR 0027 §1 / docs/07-content §7.4.
//
// Phase 3 starter set: one theme per Tier 1-5 (forest / cave / temple / tower
// / abyss). High-Tier dungeons (6+) are produced by `createDungeonTemplate`,
// which derives the numeric curves from the same Tier-indexed formulas the
// authored Tier 1-5 entries follow. Specialised dungeons (job-unlock keyed,
// raid hubs) are out of scope and authored in ADR 0029 / future work.
//
// Numeric design (暫定、Phase 4 校正前提):
//   - baseEnemyLevel = tier * 10
//   - modifierSlots  = step function: Tier 1-3 → 0, Tier 4-5 → 1,
//                      Tier 6-9 → 2, Tier 10+ → 3
//   - rewardScale    = 1.0 + (tier - 1) * 0.2  (Tier 1 = 1.0, Tier 5 = 1.8,
//                      Tier 10 = 2.8, ...)
// Theme metadata (name, flavour) is authored only for Tier 1-5; high-Tier
// generated templates inherit the theme but bump the Tier suffix.
import { DEFAULT_CAMP_SUBMENU, DEFAULT_EVENT_SUBTYPE, DEFAULT_NODE_DISTRIBUTION, DEFAULT_SUBTYPE_ROLLS, STANDARD_FLOOR_LAYOUT, } from '@renavora/shared';
/** Authored theme keys for Tier 1-5 starter templates. */
export const DUNGEON_THEMES = ['forest', 'cave', 'temple', 'tower', 'abyss'];
const THEME_METADATA = {
    forest: { key: 'forest', displayName: 'Hollowroot Grove', starterTier: 1 },
    cave: { key: 'cave', displayName: 'Dripping Hollow', starterTier: 2 },
    temple: { key: 'temple', displayName: 'Temple of Silence', starterTier: 3 },
    tower: { key: 'tower', displayName: 'Inverted Spire', starterTier: 4 },
    abyss: { key: 'abyss', displayName: 'Echoing Abyss', starterTier: 5 },
};
function baseEnemyLevelForTier(tier) {
    return tier * 10;
}
function modifierSlotsForTier(tier) {
    if (tier <= 3)
        return 0;
    if (tier <= 5)
        return 1;
    if (tier <= 9)
        return 2;
    return 3;
}
function rewardScaleForTier(tier) {
    return 1.0 + (tier - 1) * 0.2;
}
/**
 * Build a `DungeonTemplate` from a theme + tier. Used both to author the
 * Tier 1-5 starter catalogue and to materialise high-Tier (6+) templates.
 *
 * `name` falls back to the theme's display name with a Tier suffix when the
 * tier exceeds the starter Tier. The layout / distribution / subtype rolls /
 * camp submenu / event subtype default to ADR 0026 §4.2 pinned tables.
 */
export function createDungeonTemplate(theme, tier) {
    if (!Number.isInteger(tier) || tier < 1) {
        throw new Error(`tier must be a positive integer, got ${tier}`);
    }
    const meta = THEME_METADATA[theme];
    const name = tier === meta.starterTier ? meta.displayName : `${meta.displayName} +${tier}`;
    return {
        id: `dungeon.${theme}.t${tier}`,
        name,
        tier,
        baseEnemyLevel: baseEnemyLevelForTier(tier),
        modifierSlots: modifierSlotsForTier(tier),
        rewardScale: rewardScaleForTier(tier),
        layout: STANDARD_FLOOR_LAYOUT,
        distribution: DEFAULT_NODE_DISTRIBUTION,
        subtypeRolls: DEFAULT_SUBTYPE_ROLLS,
        campSubmenu: DEFAULT_CAMP_SUBMENU,
        eventSubtype: DEFAULT_EVENT_SUBTYPE,
    };
}
/**
 * Authored Tier 1-5 starter catalogue. One template per theme at its
 * `starterTier`. The Phase 3 Step 10 UI surfaces this list as the basic
 * dungeon selection; higher Tiers are reached by `createDungeonTemplate`.
 */
export const STARTER_DUNGEON_TEMPLATES = DUNGEON_THEMES.map((t) => createDungeonTemplate(t, THEME_METADATA[t].starterTier));
/** Convenience: starter templates indexed by theme key. */
export const STARTER_DUNGEON_BY_THEME = Object.fromEntries(STARTER_DUNGEON_TEMPLATES.map((d) => [d.id.split('.')[1], d]));
/**
 * Tier-0 subtype rolls: the standard pinned table with the lowest band
 * extended down to tier 0. The authored bands start at tier 1
 * (`DEFAULT_SUBTYPE_ROLLS`), and `findSubtypeRollTier` throws when no band
 * covers a tier, so the tutorial dungeon needs its own array that admits 0.
 */
const TUTORIAL_SUBTYPE_ROLLS = DEFAULT_SUBTYPE_ROLLS.map((band, index) => index === 0 ? { ...band, tierMin: 0 } : band);
/**
 * Tier-0 tutorial dungeon (ADR 0038 §2.1 / P55-042). Phase 5.x-V balance showed
 * a fresh Lv1 hero cannot solo the Tier 1 forest loop (Enemy Lv10 → 0/12). T0 is
 * an *additive* tier — existing Tier 1-5 numbering is untouched, so there is no
 * `completedDungeons` migration — that a fresh Lv1 hero can clear solo.
 *
 * It reuses the `forest` theme so enemy resolution (`themeOf` + `starterTierOf`
 * → `enemy.*.forest.t1`, plus the forest mob / elite pools) needs no new authored
 * enemies; difficulty is dialled down purely via `baseEnemyLevel`. The id keeps
 * the `dungeon.<theme>.t<n>` shape with n = 0; `resolveDungeonTemplate`
 * special-cases it and `bossLevelBoost(tier <= 0)` keeps the boss only a couple of
 * levels above the floor. `rewardScale` is deliberately low (learning dungeon,
 * anti-farm; the ADR 0038 §2.3 difficulty multiplier decays it further for
 * over-levelled re-runs).
 */
export const TUTORIAL_DUNGEON_TEMPLATE = {
    id: 'dungeon.forest.t0',
    name: 'Hollowroot Grove (Tutorial)',
    tier: 0,
    baseEnemyLevel: 2,
    modifierSlots: 0,
    rewardScale: 0.5,
    layout: STANDARD_FLOOR_LAYOUT,
    distribution: DEFAULT_NODE_DISTRIBUTION,
    subtypeRolls: TUTORIAL_SUBTYPE_ROLLS,
    campSubmenu: DEFAULT_CAMP_SUBMENU,
    eventSubtype: DEFAULT_EVENT_SUBTYPE,
};
//# sourceMappingURL=templates.js.map